import { useCallback, useEffect, useState } from 'react';
import { useServerList } from '../hooks/useServerList';
import { normalizePagination } from '../lib/pagination';
import { Pagination } from '../components/common/Pagination';
import { Package, RefreshCw, AlertTriangle, CreditCard, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';

interface Parcel {
  parcelId: string;
  carrier?: string;
  serviceLevel?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  ratedCost: number;
  weight?: { value: number; unit: string };
  dimensions?: { length: number; width: number; height: number; unit: string };
  labelPdfUrl?: string;
  orderRefs: string[];
}

interface OrderRef {
  rawInternalOrderId: string;
  resolution: 'order' | 'bulk_order' | 'unresolved';
  orderNumber?: string;
  orderStatusAtShipment?: string;
  shippingAmountAtShipment?: number;
}

/** List rows omit parcels/orders/revisions; the detail fetch fills them in. */
interface ShipmentRow {
  _id: string;
  shipmentNumber: string;
  terminalShipmentId: string;
  shipDate?: string;
  physicianId?: { profile?: { clinicName?: string; firstName?: string; lastName?: string } };
  parcels?: Parcel[];
  orders?: OrderRef[];
  parcelCount: number;
  ratedCostTotal: number;
  billedAmount: number;
  legacyPrepaidShippingTotal: number;
  status: string;
  reconciliationRequired: boolean;
  reconciliationNotes?: string;
  quarantineReason?: string;
  revisions?: { at: string; ratedCostTotal: number; applied: boolean; note?: string }[];
  billing?: { invoiceId?: any; lastChargeError?: string; chargedAt?: string };
}

const statusStyles: Record<string, string> = {
  charged: 'bg-emerald-100 text-emerald-800',
  invoiced: 'bg-blue-100 text-blue-800',
  received: 'bg-gray-100 text-gray-800',
  payment_failed: 'bg-red-100 text-red-800',
  no_charge: 'bg-amber-100 text-amber-800',
  quarantined: 'bg-purple-100 text-purple-800'
};

const PAGE_SIZE = 25;

const money = (n?: number) =>
  `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminShipments() {
  const [status, setStatus] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // Every shipment needing review, across all pages (not just the one shown).
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<ShipmentRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Search and filters run on the server; the hook debounces typing and goes
  // back to page 1 on any change.
  const list = useServerList<ShipmentRow, { status: string; flaggedOnly: boolean }>({
    pageSize: PAGE_SIZE,
    filters: { status, flaggedOnly },
    fetchPage: async ({ page, pageSize, search, filters }) => {
      const res = await apiClient.getFacilityShipments({
        status: filters.status || undefined,
        reconciliationRequired: filters.flaggedOnly || undefined,
        search: search || undefined,
        page,
        limit: pageSize
      });
      if (!res.success) {
        toast.error(res.error || 'Failed to load shipments');
        throw new Error(res.error || 'Failed to load shipments');
      }
      const items: ShipmentRow[] = (res as any).data || [];
      const p = normalizePagination((res as any).pagination, { page, pageSize, itemCount: items.length });
      return { items, total: p.total, hasMore: p.hasMore };
    }
  });
  const rows = list.items;
  // Nothing has answered yet counts as loading, so the empty state never flashes first.
  const loading = list.loading || (list.total === null && !list.error);

  const loadFlaggedCount = useCallback(async () => {
    const res = await apiClient.getFacilityShipments({ reconciliationRequired: true, limit: 1 });
    if (res.success) setFlaggedCount((res as any).pagination?.total ?? 0);
  }, []);
  useEffect(() => { loadFlaggedCount(); }, [loadFlaggedCount]);

  const load = useCallback(() => {
    list.reload();
    loadFlaggedCount();
  }, [list.reload, loadFlaggedCount]);

  const toggle = async (row: ShipmentRow) => {
    if (expanded === row._id) { setExpanded(null); setDetail(null); return; }
    setExpanded(row._id);
    setDetail(null);
    setDetailLoading(true);
    const res = await apiClient.getFacilityShipment(row._id);
    if (res.success) setDetail((res as any).data);
    else toast.error(res.error || 'Failed to load shipment detail');
    setDetailLoading(false);
  };

  const retryCharge = async (row: ShipmentRow) => {
    setBusyId(row._id);
    const res = await apiClient.retryFacilityShipmentCharge(row._id);
    if (res.success) {
      const charged = (res as any).data?.charged;
      charged
        ? toast.success(`${row.shipmentNumber} charged`)
        : toast.error((res as any).data?.error || 'Charge did not complete');
      await load();
    } else {
      toast.error(res.error || 'Failed to retry charge');
    }
    setBusyId(null);
  };

  const resolve = async (row: ShipmentRow) => {
    const note = window.prompt(`Resolve ${row.shipmentNumber} — what did you do?`);
    if (!note || !note.trim()) return;
    setBusyId(row._id);
    const res = await apiClient.resolveFacilityShipment(row._id, note.trim());
    if (res.success) {
      toast.success(`${row.shipmentNumber} resolved`);
      await load();
    } else {
      toast.error(res.error || 'Failed to resolve');
    }
    setBusyId(null);
  };


  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Package className="h-6 w-6 text-gray-500" />
            Facility Shipments
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            One row per physical dispatch from the pharmacy. The carrier cost shown is what the
            Terminal reported at label time and what the clinic was charged — Bloom never sees the
            later FedEx invoice, so these figures are not reconciled.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {flaggedCount > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>{flaggedCount}</strong> shipment{flaggedCount === 1 ? '' : 's'} need review —
            unresolved orders, a cost that changed after invoicing, or a mix of pre-paid and
            newly-billed orders. Nothing is auto-corrected; each needs a decision.
          </p>
        </div>
      )}

      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <input
          value={list.search}
          onChange={e => list.setSearch(e.target.value)}
          placeholder="Shipment #, terminal ID, or tracking…"
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-72"
        />
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All statuses</option>
          <option value="charged">Charged</option>
          <option value="invoiced">Invoiced</option>
          <option value="payment_failed">Payment failed</option>
          <option value="no_charge">Not billed</option>
          <option value="quarantined">Quarantined</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)} />
          Needs review only
        </label>
      </div>

      {loading && rows.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {list.search.trim() || status || flaggedOnly
            ? 'No shipments match these filters.'
            : 'No facility shipments yet. They appear once the pharmacy Terminal reports a dispatch.'}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const clinic = row.physicianId?.profile?.clinicName
              || [row.physicianId?.profile?.firstName, row.physicianId?.profile?.lastName].filter(Boolean).join(' ')
              || 'Unknown clinic';
            const isOpen = expanded === row._id;

            return (
              <div key={row._id} className="border border-gray-200 rounded-lg bg-white">
                <button
                  onClick={() => toggle(row)}
                  className="w-full text-left p-4 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{row.shipmentNumber}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusStyles[row.status] || 'bg-gray-100 text-gray-800'}`}>
                          {row.status.replace(/_/g, ' ')}
                        </span>
                        {row.reconciliationRequired && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Needs review
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">{clinic}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {row.shipDate ? new Date(row.shipDate).toLocaleDateString() : '—'}
                        {' · '}{row.parcelCount} parcel{row.parcelCount === 1 ? '' : 's'}
                        {' · '}{row.orders?.length ?? '—'} order{(row.orders?.length ?? 0) === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-semibold text-gray-900">{money(row.ratedCostTotal)}</div>
                      <div className="text-xs text-gray-500">
                        {row.billedAmount > 0 ? `billed ${money(row.billedAmount)}` : 'not billed'}
                      </div>
                    </div>
                  </div>
                </button>

                {isOpen && detailLoading && (
                  <div className="border-t border-gray-200 p-4 text-sm text-gray-500">Loading detail…</div>
                )}

                {isOpen && !detailLoading && detail && (
                  <div className="border-t border-gray-200 p-4 space-y-4">
                    {(detail.quarantineReason || detail.reconciliationNotes) && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                        {detail.quarantineReason && <p className="font-medium">{detail.quarantineReason}</p>}
                        {detail.reconciliationNotes && <p className="mt-1">{detail.reconciliationNotes}</p>}
                      </div>
                    )}

                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Parcels</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500">
                              <th className="py-1 pr-4">Tracking</th>
                              <th className="py-1 pr-4">Service</th>
                              <th className="py-1 pr-4">Weight</th>
                              <th className="py-1 pr-4">Dimensions</th>
                              <th className="py-1 text-right">Rated cost</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(detail.parcels || []).map(p => (
                              <tr key={p.parcelId}>
                                <td className="py-2 pr-4">
                                  {p.trackingUrl ? (
                                    <a href={p.trackingUrl} target="_blank" rel="noreferrer"
                                       className="text-blue-600 hover:underline">
                                      {p.trackingNumber || p.parcelId}
                                    </a>
                                  ) : (p.trackingNumber || p.parcelId)}
                                </td>
                                <td className="py-2 pr-4 text-gray-600">{p.serviceLevel || p.carrier || '—'}</td>
                                <td className="py-2 pr-4 text-gray-600">
                                  {p.weight ? `${p.weight.value} ${p.weight.unit}` : '—'}
                                </td>
                                <td className="py-2 pr-4 text-gray-600">
                                  {p.dimensions
                                    ? `${p.dimensions.length}×${p.dimensions.width}×${p.dimensions.height} ${p.dimensions.unit}`
                                    : '—'}
                                </td>
                                <td className="py-2 text-right font-medium">{money(p.ratedCost)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Orders in this shipment
                      </h4>
                      <ul className="text-sm space-y-1">
                        {(detail.orders || []).map(o => (
                          <li key={o.rawInternalOrderId} className="flex items-center gap-2 flex-wrap">
                            <span className={o.resolution === 'unresolved' ? 'text-red-600' : 'text-gray-900'}>
                              {o.orderNumber || o.rawInternalOrderId}
                            </span>
                            {o.resolution === 'unresolved' && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800">unresolved</span>
                            )}
                            {o.orderStatusAtShipment && (
                              <span className="text-xs text-gray-500">{o.orderStatusAtShipment}</span>
                            )}
                            {(o.shippingAmountAtShipment || 0) > 0 && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                                pre-paid {money(o.shippingAmountAtShipment)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {detail.revisions && detail.revisions.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          Corrections from the Terminal
                        </h4>
                        <ul className="text-sm space-y-1 text-gray-600">
                          {detail.revisions.map((r, i) => (
                            <li key={i}>
                              {new Date(r.at).toLocaleString()} — {money(r.ratedCostTotal)}
                              {r.applied ? ' (applied)' : ' (not applied)'}
                              {r.note ? ` · ${r.note}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {detail.billing?.lastChargeError && (
                      <p className="text-sm text-red-700">Last charge error: {detail.billing.lastChargeError}</p>
                    )}

                    <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
                      {detail.billing?.invoiceId && detail.status !== 'charged' && (
                        <button
                          onClick={() => retryCharge(row)}
                          disabled={busyId === row._id}
                          className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          <CreditCard className="h-4 w-4" />
                          Retry charge
                        </button>
                      )}
                      {detail.reconciliationRequired && (
                        <button
                          onClick={() => resolve(row)}
                          disabled={busyId === row._id}
                          className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Mark resolved
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {list.totalPages !== null && (
        <div className="mt-4">
          <Pagination
            currentPage={list.page}
            totalPages={list.totalPages}
            onPageChange={list.setPage}
            totalItems={list.total ?? undefined}
            itemsPerPage={PAGE_SIZE}
          />
        </div>
      )}
    </div>
  );
}
